import { Component, OnInit } from '@angular/core';
import { DataService } from '../../../services/data/data.service';
import { FiltersM } from '../../../components/models';
import {
  QuestionnaireCategory,
  ResultadosClimaService,
} from '../../../services/cuestionarios/resultados-clima/resultados-clima.service';

interface Tarjeta {
  title: string;
  body: string;
  preguntas: string[];
  icon: string;
}

interface Dimension {
  id: string;
  name: string;
  description: string;
  check: boolean;
  categorias: QuestionnaireCategory[];
}

// Ícono por dimensión. El back no manda ícono; mapeo manual por nombre.
const ICONOS_DIMENSION: Record<string, string> = {
  'Liderazgo': 'flag',
  'Motivación': 'bolt',
  'Empoderamiento': 'rocket_launch',
  'Toma de decisiones': 'rule',
  'Comunicación': 'forum',
  'Trabajo en equipo': 'groups',
};
const ICONO_DIMENSION_DEFECTO = 'insights';

// Ícono por indicador (categoría). Mismo criterio: el back no manda ícono.
const ICONOS_INDICADOR: Record<string, string> = {
  // Liderazgo
  'Influencia': 'trending_up',
  'Argumento': 'chat_bubble',
  'Credibilidad': 'verified',
  'Conexión personal': 'diversity_3',
  'Empatía': 'favorite',
  'Desarrollo': 'eco',
  'Valía por el esfuerzo': 'star',
  'Reconocimiento VAD': 'diamond',
  'Mérito compartido': 'emoji_events',
  'Credulidad': 'handshake',
  'Expectativas': 'track_changes',
  // Motivación
  'Alineación': 'explore',
  'Disfrute': 'sentiment_satisfied',
  'Proyección': 'visibility',
  'Entusiasmo': 'celebration',
  'Orgullo': 'workspace_premium',
  'Inspiración': 'auto_awesome',
  'Beneficio compartido': 'volunteer_activism',
  'Reconocimiento': 'military_tech',
  'Resiliencia': 'shield',
  'Trascendencia': 'all_inclusive',
  'Distinción': 'star',
  // Empoderamiento
  'Delegación': 'assignment_turned_in',
  'Independencia': 'self_improvement',
  'Pertenencia': 'home',
  'Marca': 'sell',
  'Autoconfianza': 'psychology',
  'Iniciativa': 'bolt',
  'Potenciación': 'trending_up',
  'Reciprocidad': 'sync_alt',
  'Objetivos': 'track_changes',
  'Recursos': 'inventory_2',
  // Toma de decisiones
  'Seguridad': 'verified_user',
  'Apoyo': 'support_agent',
  'Retroalimentación': 'feedback',
  'Involucramiento': 'groups',
  'Autonomía': 'directions_walk',
  'Potestad': 'gavel',
  'Oportunidad de mejora': 'trending_up',
  // Comunicación
  'Canales': 'campaign',
  'Claridad': 'light_mode',
  'Oportuna': 'schedule',
  'Relevancia': 'priority_high',
  'Escucha': 'hearing',
  'Respeto': 'handshake',
  'Confianza': 'verified',
  'Interáreas': 'hub',
  'Claridad en la acción': 'bolt',
  // Trabajo en equipo
  'Cooperación': 'handshake',
  'Camaradería': 'groups',
  'Compromiso': 'task_alt',
  'Mérito': 'emoji_events',
  'Valoración': 'thumb_up',
  'Confort': 'chair',
};
const ICONO_INDICADOR_DEFECTO = 'label';

@Component({
  selector: 'app-catalogo-dimensiones',
  templateUrl: './catalogo-dimensiones.component.html',
  styleUrl: './catalogo-dimensiones.component.css'
})
export class CatalogoDimensionesComponent implements OnInit {
  type = FiltersM;

  dimensiones: Dimension[] = [];
  dimension_select = '';
  description_dimension = '';
  icono_dimension = ICONO_DIMENSION_DEFECTO;
  tarjetas: Tarjeta[] = [];
  loading = false;
  /** Ojo del encabezado: muestra la pregunta de todas las tarjetas sin pasar el mouse una por una */
  mostrarPreguntas = false;
  // Placeholders mientras carga: dos filas del grid de 4 columnas
  readonly skeletons = new Array(8);

  constructor(
    public dataService: DataService,
    private clima: ResultadosClimaService,
  ) {}

  ngOnInit(): void {
    this.cargar();
  }

  private async cargar(): Promise<void> {
    const allocation = JSON.parse(localStorage.getItem('allocation') || 'null');
    const id = allocation?.questionary?.id;
    if (id == null) return;

    this.loading = true;
    const cuestionario = await this.clima.getQuestionnaire(Number(id));
    this.loading = false;

    if (cuestionario) this.construir(cuestionario.categories);
  }

  // Arma el árbol dimensión → categorías a partir de las categorías planas
  private construir(categorias: QuestionnaireCategory[]): void {
    const porId = (a: { id: string }, b: { id: string }) => Number(a.id) - Number(b.id);

    this.dimensiones = categorias
      .filter(c => c.parent == null)
      .sort(porId)
      .map((dim, i) => ({
        id: dim.id,
        name: dim.name,
        description: dim.description,
        check: i === 0,
        categorias: categorias.filter(c => c.parent?.id === dim.id).sort(porId),
      }));
    
    if (this.dimensiones.length) this.seleccionar(this.dimensiones[0]);
  }

  dimensionChange(seleccion: { id: string }): void {
    const dim = this.dimensiones.find(d => d.id === seleccion.id);
    if (dim) this.seleccionar(dim);
  }

  private seleccionar(dim: Dimension): void {
    this.dimensiones.forEach(d => (d.check = d.id === dim.id));
    this.dimension_select = dim.name;
    this.description_dimension = dim.description;
    this.icono_dimension = ICONOS_DIMENSION[dim.name] || ICONO_DIMENSION_DEFECTO;

    this.tarjetas = dim.categorias.map(cat => {
      const nombre = cat.name.trim();
      const preguntas = (cat.questions || [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map(q => q.description);

      return {
        title: nombre,
        body: cat.description || preguntas[0] || '',
        preguntas,
        icon: ICONOS_INDICADOR[nombre] || ICONO_INDICADOR_DEFECTO,
      };
    });
  }
}
